import Cookies from 'js-cookie'

/**
 * Loads the target route as a full page load, without handling any outstanding fetches.
 *
 * @param fullPath The full path to load.
 * @returns A never-resolving promise.
 */
function doBlockingLoad(fullPath: string) {
  // Prefixing the origin keeps `//evil.com` from being read as a protocol-relative URL
  window.location.assign(`${window.location.origin}${fullPath}`)
  return new Promise<never>(() => {})
}

export default defineNuxtRouteMiddleware(to => {
  const { $sentry } = useNuxtApp()

  const abCookieValue = Cookies.get('nf_ab')

  if (abCookieValue) {
    $sentry.setTag('ab_value', abCookieValue)
  }

  const { splitTesting } = useRuntimeConfig().public

  if (!splitTesting.branch) return

  const { id: userId, attributes: userAttributes } = storeToRefs(useUserStore())

  const routeName = (to.name as string) ?? ''

  if (userId.value && userAttributes.value.frontendVersion === splitTesting.branch) {
    // Don't set the cookie again if unchanged
    if (abCookieValue === splitTesting.branch) return

    const date = new Date()
    const expires = date.getTime() + 1000 * 3600 * 24 * 365
    date.setTime(expires)

    Cookies.set('nf_ab', splitTesting.branch, { expires: date })
    // The new frontend may send the user through its own login first, so we add the expected redirect info (guarded against absolute URLs).
    if (/^\/(?![/\\])/.test(to.fullPath)) {
      Cookies.set('redirectUrl', to.fullPath, { path: '/', sameSite: 'lax' })
    }

    return doBlockingLoad(to.fullPath)
  } else if (userId.value && abCookieValue === splitTesting.branch) {
    // We don't want to interrupt with a reload on auth routes, that will happen on next login if necessary
    if (['login', 'logout'].includes(routeName)) return

    // Reset to default branch
    Cookies.remove('nf_ab')

    return doBlockingLoad(to.fullPath)
  }
})
